/**
 * EMEDIEXP brand logo.
 *
 * One component for every placement so the mark is never re-cropped or
 * re-coloured ad hoc. Assets live in /public/brand and are transparent PNGs cut
 * from the master artwork (public/brand/emediexp.jpg), so they sit correctly on
 * white headers, tinted panels and the navy gradient alike.
 *
 *   <Logo />                      → mark + wordmark, the default header lockup
 *   <Logo variant="mark" />       → icon only (collapsed sidebar, avatar chip)
 *   <Logo variant="stacked" />    → centred mark over wordmark + tagline (auth/hero)
 *   <Logo variant="wordmark" />   → type only
 *
 * `size` is the mark's height in px; the wordmark scales off it, so a single
 * number keeps the lockup in proportion.
 */

import { Link } from "react-router-dom";

const MARK = "/brand/emediexp-mark.png";
const WORDMARK = "/brand/emediexp-wordmark.png";
const TAGLINE = "/brand/emediexp-tagline.png";
const LOCKUP = "/brand/emediexp-lockup.png";

const ALT = "EMEDIEXP — Express care. Always there.";

export type LogoVariant = "horizontal" | "mark" | "wordmark" | "stacked";

export interface LogoProps {
  variant?: LogoVariant;
  /** Height of the mark in px (the wordmark is sized relative to it). */
  size?: number;
  /** Show the "Express care. Always there." line (horizontal + stacked). */
  withTagline?: boolean;
  className?: string;
  /** Render as a link to this route. Omit for a plain, non-interactive logo. */
  to?: string;
}

export function Logo({
  variant = "horizontal",
  size = 32,
  withTagline = false,
  className = "",
  to,
}: LogoProps) {
  const content = <LogoArtwork variant={variant} size={size} withTagline={withTagline} />;

  const box =
    variant === "stacked"
      ? "inline-flex flex-col items-center gap-2"
      : "inline-flex items-center gap-2.5";

  if (to) {
    return (
      <Link
        to={to}
        aria-label={ALT}
        className={`${box} rounded-xl transition hover:opacity-85 ${className}`}
      >
        {content}
      </Link>
    );
  }

  return (
    <span className={`${box} ${className}`} aria-label={ALT} role="img">
      {content}
    </span>
  );
}

function LogoArtwork({
  variant,
  size,
  withTagline,
}: Required<Pick<LogoProps, "variant" | "size" | "withTagline">>) {
  if (variant === "mark") {
    return <img src={MARK} alt={ALT} height={size} style={{ height: size }} className="w-auto" />;
  }

  if (variant === "wordmark") {
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <img src={WORDMARK} alt={ALT} style={{ height: size * 0.5 }} className="w-auto" />
        {withTagline && (
          <img src={TAGLINE} alt="" aria-hidden style={{ height: size * 0.19 }} className="w-auto" />
        )}
      </span>
    );
  }

  if (variant === "stacked") {
    // The master lockup already contains mark + wordmark + tagline in the
    // correct proportions — use it whole rather than restacking the pieces.
    if (withTagline) {
      return <img src={LOCKUP} alt={ALT} style={{ height: size * 1.9 }} className="w-auto" />;
    }
    return (
      <>
        <img src={MARK} alt="" aria-hidden style={{ height: size }} className="w-auto" />
        <img src={WORDMARK} alt={ALT} style={{ height: size * 0.42 }} className="w-auto" />
      </>
    );
  }

  /* horizontal — mark on the left, type on the right */
  return (
    <>
      <img src={MARK} alt="" aria-hidden style={{ height: size }} className="w-auto shrink-0" />
      <span className="inline-flex flex-col items-start justify-center gap-[3px]">
        <img src={WORDMARK} alt={ALT} style={{ height: size * 0.42 }} className="w-auto" />
        {withTagline && (
          <img src={TAGLINE} alt="" aria-hidden style={{ height: size * 0.17 }} className="w-auto" />
        )}
      </span>
    </>
  );
}

/**
 * Compact square badge — the mark inside a brand-tinted tile. Used where a
 * fixed-size chip reads better than the free-standing lockup (dense toolbars,
 * list avatars, the collapsed mobile header).
 */
export function LogoBadge({ size = 36, className = "" }: { size?: number; className?: string }) {
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-xl bg-brand-50 ring-1 ring-brand-100 ${className}`}
      style={{ height: size, width: size }}
      role="img"
      aria-label={ALT}
    >
      <img src={MARK} alt="" aria-hidden style={{ height: size * 0.68 }} className="w-auto" />
    </span>
  );
}
